import {
  QAlert,
  QButton,
  QFormControl,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QStack,
  QText,
  QTextarea,
} from '@qualio/ui-components';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentCategory } from '../../../../../../../api/model/document';
import { MedtechUserV2 } from '../../../../../../../api/user';

type ChangedApprover = {
  id: number;
  full_name: string;
};

type ChangedApproversListProps = {
  users: ChangedApprover[];
  label: 'Approvers added' | 'Approvers removed';
};

const ChangedApproversList = ({ users, label }: ChangedApproversListProps) => {
  const { t } = useTranslation();
  return (
    <QStack spacing="1">
      <QText fontWeight="semibold">{label}</QText>
      {users.length > 0 ? (
        users.map((user) => <QText key={user.id}>{user.full_name}</QText>)
      ) : (
        <QText>{t('labels.none')}</QText>
      )}
    </QStack>
  );
};

export type ManageApproversConfirmationModalProps = {
  isOpen: boolean;
  modalTitle: string;
  displayWarning?: boolean;
  addedApprovers: ChangedApprover[];
  removedApprovers: ChangedApprover[];
  selections: readonly MedtechUserV2[];
  documentCategory: DocumentCategory;
  requiresComment?: boolean;
  onClose: () => void;
  onPrevious: () => void;
  onSubmit: (
    selections: readonly MedtechUserV2[],
    comment: string,
  ) => Promise<void>;
};

export const ManageApproversConfirmationModal = ({
  isOpen,
  modalTitle,
  addedApprovers = [],
  removedApprovers = [],
  selections = [],
  displayWarning = false,
  requiresComment = false,
  onClose,
  onSubmit,
  onPrevious,
  documentCategory,
}: ManageApproversConfirmationModalProps) => {
  const { t } = useTranslation();
  const [isApplyingChanges, setIsApplyingChanges] = useState(false);
  const textareaPlaceholder = useMemo(
    () =>
      removedApprovers.length > 1
        ? t('labels.reasonForRemovingApprovers')
        : t('labels.reasonForRemovingApprover'),
    [removedApprovers.length, t],
  );

  const [reason, setReason] = useState('');

  const close = () => {
    setReason('');
    onClose();
  };

  const applyChanges = async () => {
    setIsApplyingChanges(true);
    await onSubmit(selections, reason);
    setIsApplyingChanges(false);
  };

  const isIssueStepContent =
    documentCategory === DocumentCategory.ISSUE_RELATED;

  return (
    <QModal isOpen={isOpen} onClose={close}>
      <QModalHeader>
        <QText fontSize="xl">{modalTitle}</QText>
      </QModalHeader>
      <QModalBody>
        <QStack spacing="6">
          {displayWarning && (
            <QAlert
              title={
                isIssueStepContent
                  ? t('additionalMessages.thisActionAffectsEventStep')
                  : t('additionalMessages.thisActionAffectsApprovalStatus')
              }
              description={
                <>
                  {isIssueStepContent ? (
                    <QText>
                      {t(
                        'additionalMessages.removingApproversChangesEventStep',
                        {
                          status: 'effective',
                        },
                      )}
                    </QText>
                  ) : (
                    <QText>
                      {t(
                        'additionalMessages.removingApproversChangesDocumentStatus',
                        {
                          fromStatus: 'for approval',
                          toStatus: 'approved',
                          effectiveStatus: 'effective',
                        },
                      )}
                    </QText>
                  )}
                </>
              }
              status="warning"
            />
          )}
          <ChangedApproversList
            users={addedApprovers}
            label={t('labels.approversAdded')}
          />
          <ChangedApproversList
            users={removedApprovers}
            label={t('labels.approversRemoved')}
          />
          {requiresComment && (
            <QFormControl label={t('forms.reason')} isRequired>
              <QTextarea
                data-cy="manage-approver-modal-comment"
                placeholder={textareaPlaceholder}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </QFormControl>
          )}
        </QStack>
      </QModalBody>
      <QModalActions>
        <QButton
          data-cy="document-manage-approvers-cancel-button"
          variant="ghost"
          onClick={close}
          isDisabled={isApplyingChanges}
        >
          {t('buttons.cancel')}
        </QButton>
        <QButton
          data-cy="document-manage-approvers-previous-button"
          variant="outline"
          onClick={onPrevious}
          isDisabled={isApplyingChanges}
        >
          {t('buttons.previous')}
        </QButton>
        <QButton
          data-cy="document-manage-approvers-apply-changes-button"
          isLoading={isApplyingChanges}
          isDisabled={requiresComment && !reason}
          onClick={applyChanges}
        >
          {t('buttons.applyChanges')}
        </QButton>
      </QModalActions>
    </QModal>
  );
};
